import React, { useState } from "react";
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import type { BenefitsTeamMessage } from "../benefitsSummary.api";

// Must match the limits in backend/src/patientportal/benefitsTeamMessageEmail.ts
export const SUBJECT_MAX_LENGTH = 150;
export const MESSAGE_MAX_LENGTH = 5000;

type SendStatus = "idle" | "sending" | "sent" | "error";

interface MessageBenefitsTeamDialogProps {
  open: boolean;
  onClose: () => void;
  onSend: (message: BenefitsTeamMessage) => Promise<void>;
  childName: string;
}

const MessageBenefitsTeamDialog = ({
  open,
  onClose,
  onSend,
  childName,
}: MessageBenefitsTeamDialogProps) => {
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [status, setStatus] = useState<SendStatus>("idle");

  const sending = status === "sending";
  const canSend = subject.trim().length > 0 && message.trim().length > 0 && !sending;

  const close = () => {
    if (sending) return;
    onClose();
  };

  // A sent message is cleared once the dialog has closed, so the next one
  // starts blank. A draft that was not sent is kept.
  const reset = () => {
    if (status !== "sent") return;
    setSubject("");
    setMessage("");
    setStatus("idle");
  };

  const send = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!canSend) return;
    setStatus("sending");
    try {
      await onSend({ subject: subject.trim(), message: message.trim() });
      setStatus("sent");
    } catch {
      setStatus("error");
    }
  };

  return (
    <Dialog
      open={open}
      onClose={close}
      fullWidth
      maxWidth="sm"
      aria-labelledby="message-benefits-team-title"
      slotProps={{ transition: { onExited: reset } }}
    >
      <DialogTitle id="message-benefits-team-title">
        Message the Benefits Team
      </DialogTitle>

      {status === "sent" ? (
        <>
          <DialogContent>
            <Stack direction="row" spacing={1.5} alignItems="flex-start">
              <CheckCircleOutlinedIcon color="success" />
              <Typography>
                Your message was sent. Our benefits team will reply to the email
                you use to sign in to the portal.
              </Typography>
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button variant="contained" onClick={close}>
              Close
            </Button>
          </DialogActions>
        </>
      ) : (
        <form onSubmit={send} noValidate>
          <DialogContent>
            <Stack spacing={2.5}>
              <Typography variant="body2" color="text.secondary">
                Ask a question about {childName}'s benefits summary. Our team will
                reply to the email you use to sign in to the portal.
              </Typography>
              {status === "error" && (
                <Alert severity="error">
                  We couldn't send your message. Please try again.
                </Alert>
              )}
              <TextField
                id="benefits-team-message-subject"
                label="Subject"
                value={subject}
                onChange={(event) => setSubject(event.target.value)}
                required
                fullWidth
                autoFocus
                disabled={sending}
                slotProps={{ htmlInput: { maxLength: SUBJECT_MAX_LENGTH } }}
              />
              <TextField
                id="benefits-team-message-body"
                label="Message"
                value={message}
                onChange={(event) => setMessage(event.target.value)}
                required
                fullWidth
                multiline
                minRows={5}
                disabled={sending}
                helperText={`${message.length}/${MESSAGE_MAX_LENGTH}`}
                slotProps={{
                  htmlInput: { maxLength: MESSAGE_MAX_LENGTH },
                  formHelperText: { sx: { textAlign: "right" } },
                }}
              />
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={close} disabled={sending}>
              Cancel
            </Button>
            <Button type="submit" variant="contained" disabled={!canSend} loading={sending}>
              Send message
            </Button>
          </DialogActions>
        </form>
      )}
    </Dialog>
  );
};

export default MessageBenefitsTeamDialog;
